import * as React from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/router';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

import { ButtonLink } from '@/components/common';
import { Button, Checkbox } from '@/components/ui';
import { useLeavePrevention } from '@/hooks/useLeavePrevention';
import { ERROR, WARN } from '@/utils/form';
import { trpc } from '@/utils/trpc';

import { RouterOutput } from '../../../trpc/router';

export type VideoDeleteFormValues = { confirm: boolean };

const schema: z.ZodType<VideoDeleteFormValues> = z.lazy(() =>
  z.object({ confirm: z.boolean({ required_error: ERROR.REQUIRED }) }),
);

export interface VideoDeleteFormProps {
  video: RouterOutput['videos']['byId'];
}

export const VideoDeleteForm = React.memo<VideoDeleteFormProps>(({ video }) => {
  const router = useRouter();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
  } = useForm<VideoDeleteFormValues>({ resolver: zodResolver(schema) });
  const watchConfirm = watch('confirm');
  const { mutateAsync } = trpc.videos.deleteProcedure.useMutation();

  useLeavePrevention(isDirty && !isSubmitting && !isSubmitSuccessful, WARN.UNSAVED_CHANGES);

  const onSubmit = React.useCallback<SubmitHandler<VideoDeleteFormValues>>(
    ({ confirm }) => confirm && mutateAsync({ id: video.id }).then(() => router.push(`/admin/videos`)),
    [mutateAsync, video.id, router],
  );

  if (video._count.workout) {
    return (
      <div className='bg-khaki-1 rounded-lg p-8 overflow-hidden' onSubmit={handleSubmit(onSubmit)}>
        <h2 className='font-josefin font-bold text-2xl tracking-tighter mb-10'>You cannot delete {video.title}.</h2>
        <div className='flex gap-x-6 mt-2 -mx-8 -mb-8 p-8 py-5 rounded-bl-lg bg-khaki-2-3'>
          <ButtonLink href={`/admin/videos/${video.id}`} variant='secondary'>
            Back
          </ButtonLink>
        </div>
      </div>
    );
  }

  return (
    <form className='bg-khaki-1 rounded-lg p-8 overflow-hidden' onSubmit={handleSubmit(onSubmit)}>
      <h2 className='font-josefin font-bold text-2xl tracking-tighter'>
        Are you sure you want to delete {video.title}?
      </h2>
      <Checkbox errors={errors} label='I understand this action can not be undone.' {...register('confirm')} />
      <div className='flex gap-x-6 mt-2 -mx-8 -mb-8 p-8 py-5 rounded-bl-lg bg-khaki-2-3'>
        <Button
          disabled={isSubmitting || !watchConfirm}
          state={isSubmitting ? 'waiting' : undefined}
          type='submit'
          variant='danger'
        >
          Yes, delete this video
        </Button>
        <ButtonLink href={`/admin/videos/${video.id}`} type='button' variant='minimal'>
          No, I changed my mind
        </ButtonLink>
      </div>
    </form>
  );
});

VideoDeleteForm.displayName = 'VideoDeleteForm';
